import React, { FC, Fragment, useCallback, useContext, useMemo, useState } from "react";
import { Page, Text, View, Document, StyleSheet, Font, Image, pdf } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";
import { PurchaseOrderItem } from "@/types/purchase";
import dayjs from "dayjs";
import { DictContext } from "@/layout/context/DictContext";
import zioncom from 'images/system/zioncom.png'
import api from "@/apis/purchase";
import { App, Button, Checkbox, CheckboxOptionType, Dropdown, Flex, Modal, notification, Space } from "antd";
import { upload } from "@/utils/file/upload";
import { getFileUrl } from "@/apis/file";
import { divided, multi } from "@/utils/calc";
import { numFormmater } from "@/pages/Requirement/util";
import convertTo2DArray from "@/utils/convert2double";
// import JsBarcode from "jsbarcode";
import QRCode from "qrcode";
import { truncateString } from "@/utils/stringOver";
import JsBarcode from "jsbarcode";
import { UserLangEnum } from "@/types/user";
import i18next from "i18next";
import ExportProgressModal from "@/components/ExportProgressModal";
import { ExclamationCircleTwoTone } from "@ant-design/icons";
import { downloadFile, downloadFileBatch } from "@/components/SDoc/utils";
import fontSrc from '@/assets/fonts/msyh.ttf';
import vnSrc from '@/assets/fonts/NotoSans-Regular.ttf';
import krSrc from '@/assets/fonts/NotoSansKR-Regular.otf';

// 注册支持中文的字体（重复注册幂等；若启动预热失败，首次渲染时在此兜底加载）
Font.register({
  family: "NotoSansSC",
  src: fontSrc
});

Font.register({
  family: "VnFontSrc",
  src: vnSrc
});

Font.register({
  family: "KoFontSrc",
  src: krSrc
});


Font.registerHyphenationCallback((word: string) => ['', word, '']);


// 定义 PDF 文档样式
const styles = StyleSheet.create({
  // 页面样式
  page: {
    position: "relative",
    fontFamily: ["VnFontSrc", "KoFontSrc", "NotoSansSC"],
    padding: 28
  },

  // 表格样式
  table: {
    display: "table" as any,
    width: "100%",
    borderStyle: "solid",
    borderLeftWidth: 0,
    borderTopWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    borderColor: "black"
  },
  tableRow: {
    flexDirection: "row",
    borderLeftWidth: 1,
  },

  // 单元格基础样式
  tableCol: {
    borderStyle: "solid",
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: "black",
    padding: 2,
    fontSize: 8,
    fontFamily: ["VnFontSrc", "KoFontSrc", "NotoSansSC"],
    letterSpacing: 0,
    color: '#000000'
  },

  // 表头文本
  tableHeaderCell: {
    fontFamily: ["VnFontSrc", "KoFontSrc", "NotoSansSC"],
    letterSpacing: -1,
    fontSize: 8
  },

  // 合并单元格
  mergeCell: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    padding: 0
  },

  // 编号单元格
  noCell: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },

  // 文本样式
  fontStyle: {
    textAlign: 'center',
    letterSpacing: -1
  },

  // 页面标题
  pageTitle: {
    marginLeft: 12,
    fontSize: 12,
    textAlign: 'center',
    letterSpacing: -1,
    fontWeight: 1000
  },
  pageSubTitle: {
    fontSize: 8,
    textAlign: 'center'
  },

  // 页脚
  footer: {
    position: "absolute",
    bottom: 24,
    left: 0,
    right: 0,
    fontSize: 6,
  },

  footerItem: { width: '33.3%', fontSize: 8 },
  headerItem: { marginRight: 30, fontSize: 8, textAlign: 'left' },
  ruleText: { fontSize: 7, fontWeight: 200, letterSpacing: -1, fontFamily: ["VnFontSrc", "KoFontSrc", "NotoSansSC"] },
  ruleTextTitle: { fontSize: 9, fontWeight: 700 },
  ruleDivider: { height: 0, backgroundColor: "#000", marginVertical: 0 },
  ruleMt12: { marginTop: 1 },
  ruleMb12: { marginBottom: 1 },
  ruleMv12: { marginVertical: 1 },
  ruleTextW100: { width: "100%" },
  ruleUlContainer: { flexDirection: "row", alignItems: "flex-start" },
  ruleUl: { height: 8, width: 20, flexDirection: "row", justifyContent: "center", alignItems: "center", },
  ruleDot: { width: 4, height: 4, borderRadius: 2, backgroundColor: "#000" },
  ruleRow: { flex: 1 }
});

// 填充空格
function getBlankWord(word: string, type = false) {
  if (type) return String(word).replace(/./g, (word) => word + ' ');
  else return String(word).replace(/./g, (word) => word + '');
}

type ResPdfIdType = Array<{ id: string; fileId: string; printOrderId: string }>;

interface PrintOrderListProps {
  selectedRows: Array<PurchaseOrderItem>;
  previewMode?: boolean;
  handleStart: () => void;
  handleEnd: () => void;
  handleOk: (printItem: ResPdfIdType) => void;
  clickValid?: () => boolean
}
interface CodeUrlOrder extends PurchaseOrderItem {
  codeStr: string;
  codeUrl: string;
}

// 生成采购文件时，拒绝单需要被排除，但待处理单仍允许继续打印。
const splitPrintableRows = (rows: Array<PurchaseOrderItem>) => {
  const validRows: Array<PurchaseOrderItem> = [];
  const rejectedRows: Array<PurchaseOrderItem> = [];

  rows.forEach(row => {
    if (row?.order?.acceptState === 0) {
      rejectedRows.push(row);
      return;
    }

    validRows.push(row);
  });

  return {
    validRows,
    rejectedRows,
    rejectedCount: rejectedRows.length
  };
};

// 生成 PDF 下载链接
const PDFGenerator: FC<PrintOrderListProps> = ({
  selectedRows,
  previewMode = false,
  handleOk,
  handleStart,
  handleEnd,
  clickValid = () => true
}) => {
  const { t } = useTranslation();

  const { purchaseOrderTypes, purchasePayMethod, currencys, exchangeRate, printSetting, purchaseRule } = useContext(DictContext);
  const [loading, setLoading] = useState<boolean>(false)
  const [openPrinterLoading, setOpenPrinterLoading] = useState<boolean>(false);
  const [openOption, setOpenOption] = useState<boolean>(false);
  const [printLangs, setPrintLangs] = useState<Array<UserLangEnum>>([]);
  const [progress, setProgress] = useState<number>(0);
  const [resPdfIds, setResPdfIds] = useState<ResPdfIdType>([]);
  const [openDownload, setOpenDownload] = useState<boolean>(false);

  // 页面头部统计和后续生成都只基于可打印的有效行。
  const printableRows = useMemo(
    () => splitPrintableRows(selectedRows).validRows,
    [selectedRows]
  );

  const getMainPrice = (item: PurchaseOrderItem) => {
    const itemRateAmount = item.rateAmount;
    const itemCurr = item.order.currency;
    if (!itemCurr) return ''

    const rate = divided(exchangeRate.find(e => e.desc === "mainCurrency")?.value || '', exchangeRate.find(e => e.key === itemCurr)?.value || '');
    let resPrice = multi(rate, itemRateAmount);

    if (resPrice) return resPrice;
    return ''
  }

  // 统计信息按分组数据（datas）计算，保证分组后每个 PDF 的合计正确。
  const getSummary = useCallback((datas: Array<PurchaseOrderItem>) => {
    let num = 0;
    let totalPrice = 0;
    let totalMainPrice = 0;

    datas.forEach((row) => {
      num = (row.count ?? 0) + num;
      totalPrice = totalPrice + Number(row?.rateAmount ?? 0)
      totalMainPrice = totalMainPrice + Number(getMainPrice(row))
    })

    let orderTime = ''
    let payMethod = ''
    let today = dayjs().format('YYYY-MM-DD')
    if (datas.length > 0) {
      orderTime = getBlankWord(dayjs(datas[0].createdAt).format('YYYY-MM-DD') ?? '')
      payMethod = getBlankWord(purchasePayMethod?.find(type => type.id === datas[0].order.payMethod)?.desc ?? '')
    }
    return { num, totalPrice, totalMainPrice, orderTime, payMethod, today }
  }, [purchasePayMethod])

  const { picUrl, name, address } = useMemo(() => {
    let [picUrl, name, address] = ['', '', ''];
    if (printSetting?.length > 0) {
      printSetting.forEach(item => {
        if (item.key === "companyName" && item.value) name = item.value
        if (item.key === "companyLocation" && item.value) address = item.value
        if (item.key === "companyLogo" && item.value) picUrl = getFileUrl(item.value) || ''
      })
    }

    return { picUrl, name, address };

  }, [printSetting]);


  const printLangsOption: CheckboxOptionType<UserLangEnum>[] = useMemo(() => {
    let keys: CheckboxOptionType<UserLangEnum>[] = [];
    let labelMap: Record<UserLangEnum, string> = {
      [UserLangEnum.ZH]: "简体中文",
      [UserLangEnum.TR]: "繁體中文",
      [UserLangEnum.EN]: "English",
      [UserLangEnum.KO]: "한국인",
      [UserLangEnum.VN]: "Tiếng Việt",
    }
    purchaseRule.forEach(p => {
      let key = p.key as UserLangEnum;
      !!p.value && keys.push({
        label: labelMap[key],
        value: key
      })
    });
    return keys;
  }, [purchaseRule]);

  const mainCur = useMemo(() => {
    if (exchangeRate?.length > 0 && currencys?.length > 0) {
      let key = exchangeRate.find(d => d.desc === "mainCurrency")?.key
      if (key) return currencys.find(c => c.key === key)?.desc ?? ''
    }
    return ''

  }, [currencys, exchangeRate]);

  const handleLangs = (langs: UserLangEnum[]) => {
    setPrintLangs(langs);
  }

  // 生成条码
  const handleCode = (orderNo: string): Promise<{ codeUrl: string, codeStr: string }> => {
    return new Promise((resolve, reject) => {
      if (!orderNo || orderNo === '-') {
        resolve({ codeUrl: '', codeStr: '' });
        return;
      }
      const canvasDom = document.createElement("canvas");
      JsBarcode(canvasDom, orderNo, {
        format: "CODE128",
        width: 10,
        height: 20,
        displayValue: false,
        margin: 0,
        marginBottom: 6
      });
      canvasDom.toBlob((blob) => {
        canvasDom.remove();
        if (blob) {
          resolve({ codeStr: orderNo, codeUrl: URL.createObjectURL(blob) });
        } else {
          reject("条码生成失败");
        }
      }, 'image/png');
    });
  };

  //生成二维码
  const handleQrCode = async (orderNo: string): Promise<{ codeUrl: string, codeStr: string }> => {
    try {
      if (!orderNo.trim()) {
        return Promise.resolve({ codeUrl: '', codeStr: orderNo });
      }

      // 使用 QRCode.toDataURL 方法生成二维码图片的 Data URL
      const url = await QRCode.toDataURL(orderNo, {
        width: 100,
        margin: 0,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });

      return Promise.resolve({ codeStr: orderNo, codeUrl: url });

    } catch (err) {
      console.error(err);
      return Promise.reject('');
    }
  };
  const getSPrice = (item: PurchaseOrderItem) => {
    const sPrice = item.quotationRequest && item.quotationRequest.supplierPrice ? String(item.quotationRequest.supplierPrice) : (item?.price?.price || '')
    if (item.taxRate) {
      const rate = divided(item.taxRate ?? '0', '100')
      const amountPtrice = divided(sPrice, (1 + Number(rate)).toString())
      return numFormmater(Number(amountPtrice))
    }
    else return sPrice
  }
  const MyNormalDocument = useCallback((datas: CodeUrlOrder[], addRule: boolean) => {
    let seq = 0;
    const { num, totalPrice, totalMainPrice, orderTime, payMethod, today } = getSummary(datas);
    let supplier = datas[0]?.order?.supplier;
    let nameTemp = supplier.name || supplier.shortName;
    let supplierName = !!!nameTemp ? supplier.code : `${nameTemp}(${supplier.code})`
    let supplierContact = supplier?.contact || ''
    let billingDays = supplier.billingDays ? supplier.billingDays + t('requirement.day') : ''
    return <Document>
      <Page orientation="landscape" size="A4" style={{ ...styles.page }} wrap>
        <View fixed>
          <View><Text style={{ ...styles.fontStyle, ...styles.pageTitle }}>
            {`${name} - - (${t('purchase.purchaseList', { lng: "zh" })}/${t('purchase.purchaseList', { lng: "vn" })})`}
            {previewMode ? `【${t('requirement.preview', { lng: "zh" })}/${t('requirement.preview', { lng: "vn" })}】` : ''}
          </Text></View>
        </View>
        <View style={{ display: 'flex', justifyContent: "flex-start", flexDirection: 'row', flexWrap: "wrap", paddingTop: 1, paddingLeft: 100, }} fixed>
          <Text style={styles.headerItem}>
            {`${t('purchase.orderTime1', { lng: "zh" })}(${t('purchase.orderTime1', { lng: "vn" })}): ${orderTime}`}
          </Text>
          <Text style={styles.headerItem}>
            {`${t('requirement.supplier', { lng: "zh" })}(${t('requirement.supplier', { lng: "vn" })}): ${supplierName}`}
          </Text>
          <Text style={styles.headerItem}>
            {`${t('sale.contact', { lng: "zh" })}(${t('sale.contact', { lng: "vn" })}): ${supplierContact}`}
          </Text>
          <Text style={styles.headerItem}>
            {`${t('purchase.payMethodName', { lng: "zh" })}(${t('purchase.payMethodName', { lng: "vn" })}): ${payMethod}`}
          </Text>
          <Text style={styles.headerItem}>
            {`${t('purchase.billingDay', { lng: "zh" })}(${t('purchase.billingDay', { lng: "vn" })}): ${billingDays}`}
          </Text>
          <Text style={styles.headerItem}>
            {'TEL: 36861188'}
          </Text>
          <Text style={styles.headerItem}>
            {'FAX: 36861133'}
          </Text>
          <Text style={styles.headerItem}>
            {`${t('purchase.sendAds', { lng: "zh" })}(${t('purchase.sendAds', { lng: "vn" })}): ${address}`}
          </Text>
        </View>
        <View style={{ ...styles.table, flexShrink: 0 }}>
          <View style={{ flexDirection: 'row' }} fixed render={({ pageNumber }) => {
            return pageNumber === 1 ? null : <View style={{ height: 1, backgroundColor: "#000", width: "100%" }} />
          }}></View>
          {/* 表身 */}
          {datas.map((item, rowIndex) => (
            <View style={styles.tableRow} key={rowIndex} wrap={false}>
              {/* 左侧：序号 */}
              <View style={{ ...styles.tableCol, width: '2%', flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                <Text style={{ ...styles.fontStyle, fontSize: 8 }}>{++seq}</Text>
              </View>

              {/* 右侧：内容区域 */}
              <View style={{ ...styles.tableCol, width: '98%', flexDirection: 'column', padding: 0, borderRightWidth: 1 }}>
                {/* 上部分：订单号（有值）、交货日期（有值），后面都只有标签 */}
                <View style={{ width: '100%', borderBottomWidth: 1, flexDirection: 'row' }}>
                  {/* 订单号标签 */}
                  <View style={{ width: '10%', borderRightWidth: 1, paddingHorizontal: 2, flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                    <Text style={styles.fontStyle}>{getBlankWord(t('purchase.orderNum', { lng: "zh" }) ?? '', true)}</Text>
                    <Text style={styles.fontStyle}>{getBlankWord(t('purchase.orderNum', { lng: "vn" }) ?? '', true)}</Text>
                  </View>
                  {/* 订单号值（二维码+编号） */}
                  <View style={{ width: '16%', borderRightWidth: 1, flexDirection: 'column', alignItems: 'center', justifyContent: "center" }}>
                    {item.codeUrl && false && <Image src={item.codeUrl} style={{ width: "90%", height: 24, marginBottom: -4 }} />}
                    <Text style={[styles.fontStyle, { letterSpacing: -1, paddingHorizontal: 2 }]}>{getBlankWord(item.number ?? '', true)}</Text>
                    {/*  原厂代码 */}
                    {item?.mpn &&
                      <>
                        <View style={{ width: '100%', borderBottomWidth: 1, marginVertical: 2 }}></View>
                        <Text style={[styles.fontStyle, { letterSpacing: -1, paddingHorizontal: 2 }]}>{getBlankWord(truncateString(item?.mpn ?? '', 22), true)}</Text>
                      </>
                    }
                  </View>
                  {/* 品牌信息 */}
                  <View style={{ width: '13%', borderRightWidth: 1, flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                    <Text style={[styles.fontStyle, { paddingHorizontal: 2 }]}>{getBlankWord(truncateString(item.brand ?? '', 8), true)}</Text>
                    {/* 保质期 */}
                    <View style={{ width: '100%', borderBottomWidth: 1, marginVertical: 2 }} />
                    <View style={{ width: '100%' }}>
                      <Text style={[{ paddingHorizontal: 2, textAlign: "center" }]}>{`${truncateString(item?.quotationRequest?.price?.dcc ?? '', 10)}`}</Text>
                    </View>
                  </View>
                  {/* 交货日期标签 */}
                  <View style={{ width: '10%', borderRightWidth: 1, paddingHorizontal: 2, justifyContent: "center", alignItems: "center" }}>
                    <Text style={styles.fontStyle}>{getBlankWord(t('purchase.deliveryTime', { lng: "zh" }) ?? '', true)}</Text>
                    <Text style={styles.fontStyle}>{getBlankWord(t('purchase.deliveryTime', { lng: "vn" }) ?? '', true)}</Text>
                  </View>
                  {/* 交货日期值 */}
                  <View style={{ width: '10%', borderRightWidth: 1, paddingHorizontal: 0, justifyContent: "center", alignItems: "center" }}>
                    <Text style={styles.fontStyle}>{getBlankWord(item?.deliveryDeadline ? dayjs(item?.deliveryDeadline).format('YYYY-MM-DD') : '')}</Text>
                  </View>
                  {/* 单位标签 */}
                  <View style={{ width: '4%', borderRightWidth: 1, paddingHorizontal: 2, justifyContent: "center", alignItems: "center" }}>
                    <Text style={styles.fontStyle}>{getBlankWord(t('purchase.printUnit', { lng: "zh" }) ?? '', true)}</Text>
                    <Text style={styles.fontStyle}>{getBlankWord(t('purchase.printUnit', { lng: "vn" }) ?? '', true)}</Text>
                  </View>
                  {/* 币种标签 */}
                  <View style={{ width: '4%', borderRightWidth: 1, paddingHorizontal: 2, justifyContent: "center", alignItems: "center" }}>
                    <Text style={styles.fontStyle}>{getBlankWord(t('purchase.curr', { lng: "zh" }) ?? '', true)}</Text>
                    <Text style={styles.fontStyle}>{getBlankWord(t('purchase.curr', { lng: "vn" }) ?? '', true)}</Text>
                  </View>
                  {/* 未税单价标签 */}
                  <View style={{ width: '9%', borderRightWidth: 1, paddingHorizontal: 8, justifyContent: "center", alignItems: "center" }}>
                    <Text style={styles.fontStyle}>{getBlankWord(t('purchase.unRatePrice', { lng: "zh" }) ?? '', true)}</Text>
                    <Text style={styles.fontStyle}>{getBlankWord(t('purchase.unRatePrice', { lng: "vn" }) ?? '', true)}</Text>
                  </View>
                  {/* 税率%标签 */}
                  <View style={{ width: '6%', borderRightWidth: 1, paddingHorizontal: 2, flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                    <Text style={styles.fontStyle}>{getBlankWord(t('purchase.taxRate1', { lng: "zh" }) ?? '', true)}</Text>
                    <Text style={styles.fontStyle}>{getBlankWord(t('purchase.taxRate1', { lng: "vn" }) ?? '', true)}</Text>
                    <Text style={styles.fontStyle}>(%)</Text>
                  </View>
                  {/* 含税金额标签 */}
                  <View style={{ width: '10%', borderRightWidth: 1, paddingHorizontal: 8, justifyContent: "center", alignItems: "center" }}>
                    <Text style={styles.fontStyle}>{getBlankWord(t('purchase.printSPrice', { lng: "zh" }) ?? '', true)}</Text>
                    <Text style={styles.fontStyle}>{getBlankWord(t('purchase.printSPrice', { lng: "vn" }) ?? '', true)}</Text>
                  </View>
                  {/* 本位币金额标签 */}
                  <View style={{ width: '8%', paddingHorizontal: 2, flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                    <Text style={styles.fontStyle}>{getBlankWord(`${t('purchase.mainCurPrice', { lng: "zh" })}`, true)}</Text>
                    <Text style={styles.fontStyle}>{getBlankWord(`${t('purchase.mainCurPrice', { lng: "vn" })}`, true)}</Text>
                    <Text style={styles.fontStyle}>{getBlankWord(`(${mainCur})`, true)}</Text>
                  </View>
                </View>

                {/* 中部分：料号、品名、数量，以及上部分标签对应的值 */}
                <View style={{ width: '100%', borderBottomWidth: 1, flexDirection: 'row' }}>
                  {/* 料号标签（宽度与订单号标签一致8%） */}
                  <View style={{ width: '10%', borderRightWidth: 1, paddingHorizontal: 2, justifyContent: "center", alignItems: "center" }}>
                    <Text style={{ ...styles.fontStyle }}>
                      {getBlankWord(`${t('purchase.printCode', { lng: "zh" })}`, true)}
                      {getBlankWord(`${t('purchase.printCode', { lng: "vn" })}`, true)}
                    </Text>
                  </View>
                  {/* 料号值、品名标签、品名值（三列宽度合计24%，与订单号值一致） */}
                  {/* 料号值 */}
                  <View style={{ width: '10%', borderRightWidth: 1, paddingHorizontal: 2, justifyContent: "center", alignItems: "center" }}>
                    <Text style={{ ...styles.fontStyle, letterSpacing: -1 }}>{getBlankWord(item.product?.code ?? '', true)}</Text>
                  </View>
                  {/* 品名标签 */}
                  <View style={{ width: '6%', borderRightWidth: 1, paddingHorizontal: 2, justifyContent: "center", alignItems: "center" }}>
                    <Text style={styles.fontStyle}>{getBlankWord(t('purchase.printName', { lng: "zh" }) ?? '', true)}</Text>
                    <Text style={styles.fontStyle}>{getBlankWord(t('purchase.printName', { lng: "vn" }) ?? '', true)}</Text>
                  </View>
                  {/* 品名值 */}
                  <View style={{ width: '17%', borderRightWidth: 1, paddingHorizontal: 2, justifyContent: "center", alignItems: "center" }}>
                    <Text style={{ ...styles.fontStyle, letterSpacing: -1 }}>{getBlankWord(item.product?.name ?? '', true)}</Text>
                  </View>
                  {/* 交货日期标签对应的区域 - 数量标签和值 */}
                  {/* 数量标签（宽度与交货日期标签一致6%） */}
                  <View style={{ width: '6%', borderRightWidth: 1, paddingHorizontal: 2, justifyContent: "center", alignItems: "center" }}>
                    <Text style={styles.fontStyle}>{getBlankWord(t('purchase.printNum', { lng: "zh" }) ?? '', true)}</Text>
                    <Text style={styles.fontStyle}>{getBlankWord(t('purchase.printNum', { lng: "vn" }) ?? '', true)}</Text>
                  </View>
                  {/* 数量值（宽度与交货日期值一致16%） */}
                  <View style={{ width: '10%', borderRightWidth: 1, paddingHorizontal: 0, justifyContent: "center", alignItems: "center" }}>
                    <Text style={{ ...styles.fontStyle, letterSpacing: -1 }}>{getBlankWord(numFormmater(item.count), true)}</Text>
                  </View>
                  {/* 单位标签对应的区域 - 单位值 */}
                  <View style={{ width: '4%', borderRightWidth: 1, paddingHorizontal: 2, justifyContent: "center", alignItems: "center" }}>
                    <Text style={{ textAlign: "center" }}>{getBlankWord(item.product?.uom ?? '')}</Text>
                  </View>
                  {/* 币种标签对应的区域 - 币种值 */}
                  <View style={{ width: '4%', borderRightWidth: 1, paddingHorizontal: 0, justifyContent: "center", alignItems: "center" }}>
                    <Text style={{ textAlign: "center" }}>{currencys.find(c => c.key === item?.order?.currency)?.desc}</Text>
                  </View>
                  {/* 未税单价标签对应的区域 - 未税单价值 */}
                  <View style={{ width: '9%', borderRightWidth: 1, paddingHorizontal: 2, justifyContent: "center", alignItems: "center" }}>
                    <Text style={{ ...styles.fontStyle, letterSpacing: -1 }}>{getBlankWord(getSPrice(item), true)}</Text>
                  </View>
                  {/* 税率%标签对应的区域 - 税率值 */}
                  <View style={{ width: '6%', borderRightWidth: 1, paddingHorizontal: 0, justifyContent: "center", alignItems: "center" }}>
                    <Text style={{ ...styles.fontStyle, letterSpacing: -1 }}>{getBlankWord(item?.taxRate ?? '', true)}</Text>
                  </View>
                  {/* 含税金额标签对应的区域 - 含税金额值 */}
                  <View style={{ width: '10%', borderRightWidth: 1, paddingHorizontal: 0, justifyContent: "center", alignItems: "center" }}>
                    <Text style={{ ...styles.fontStyle, letterSpacing: -1 }}>{getBlankWord(numFormmater(Number(
                      item.quotationRequest && item.quotationRequest.supplierPrice ? String(item.quotationRequest.supplierPrice) : (item?.price?.price || '')
                    )), true)}</Text>
                  </View>
                  {/* 本位币金额标签对应的区域 - 本位币金额值 */}
                  <View style={{ width: '8%', paddingHorizontal: 0, justifyContent: "center", alignItems: "center" }}>
                    <Text style={{ ...styles.fontStyle, letterSpacing: -1 }}>{getBlankWord(numFormmater(Number(getMainPrice(item))), true)}</Text>
                  </View>
                </View>

                {/* 下部分：规格、备注 */}
                <View style={{ width: '100%', flexDirection: 'row' }}>
                  {/* 规格标签（宽度8%） */}
                  <View style={{ width: '10%', borderRightWidth: 1, paddingHorizontal: 2, justifyContent: "center", alignItems: "center" }}>
                    <Text style={styles.fontStyle}>{getBlankWord(`${t('purchase.spec', { lng: "zh" })}(${t('purchase.spec', { lng: "vn" })})`, true)}</Text>
                  </View>
                  {/* 规格值（宽度46%） */}
                  <View style={{ width: '53%', borderRightWidth: 1, paddingHorizontal: 2, justifyContent: "center", alignItems: "flex-start" }}>
                    <Text style={{ ...styles.fontStyle, letterSpacing: -1, textAlign: "left" }}>{getBlankWord(truncateString(item.product?.model ?? '', -1), true)}</Text>
                  </View>
                  {/* 备注标签（宽度6%） */}
                  <View style={{ width: '10%', borderRightWidth: 1, paddingHorizontal: 2, justifyContent: "center", alignItems: "center" }}>
                    <Text style={styles.fontStyle}>{getBlankWord(`${t('purchase.remark', { lng: "zh" })}(${t('purchase.remark', { lng: "vn" })})`, true)}</Text>
                  </View>
                  {/* 备注值（宽度40%） */}
                  <View style={{ width: '27%', paddingHorizontal: 2, justifyContent: "center", alignItems: "flex-start" }}>
                    <Text style={{ ...styles.fontStyle, letterSpacing: -1, textAlign: "left" }}>{getBlankWord((item.remark ? item.remark + '+' : '') + (item?.order?.remark ?? ''), true)}</Text>
                  </View>
                </View>
              </View>
            </View>
          ))}
          {/* 合计 */}
          <View style={{ ...styles.tableRow }} wrap={false}>
            <View style={{ ...styles.tableCol, width: '2%', fontSize: 8, justifyContent: "center", }}></View>
            <View style={{ ...styles.tableRow, width: '98%', padding: 0, borderRightWidth: 1, borderLeftWidth: 0 }}>
              <View style={{ ...styles.tableCol, width: '26%', borderRightWidth: 1 }} />
              <View style={{ ...styles.tableCol, width: '37%', borderRightWidth: 1, justifyContent: "center", paddingHorizontal: 2 }}>
                <Text style={{ ...styles.fontStyle, textAlign: "right", letterSpacing: -1 }}>
                  {getBlankWord(`${t('purchase.numAdds', { lng: "zh" })}(${t('purchase.numAdds', { lng: "vn" })}): ${numFormmater(num)}`, true)}
                </Text>
              </View>
              <View style={{ ...styles.tableCol, width: '37%', justifyContent: "center", paddingHorizontal: 2, borderRightWidth: 0 }}>
                <Text style={{ ...styles.fontStyle, textAlign: "right", letterSpacing: -1 }}>
                  {getBlankWord(`${t('purchase.ratePceAdds', { lng: "zh" })}(${t('purchase.ratePceAdds', { lng: "vn" })}): ${numFormmater(totalPrice)}`, true)}
                </Text>
              </View>
            </View>
          </View>
          {/* <View style={{ ...styles.tableRow }}>
              <View style={{ ...styles.tableCol, width: '35%', fontSize: 8 }}><Text style={{ ...styles.fontStyle, textAlign: "left" }}>{getBlankWord(t('purchase.printNum') + t('purchase.total')) + "：" + numFormmater(num)}</Text></View>
              <View style={{ ...styles.tableCol, width: '30%', fontSize: 8 }}><Text style={{ ...styles.fontStyle, textAlign: "left" }}>{getBlankWord(t('purchase.printPrce') + t('purchase.total')) + "：" + numFormmater(totalPrice)}</Text></View>
              <View style={{ ...styles.tableCol, width: '35%', fontSize: 8, borderRightWidth: 1 }}><Text style={{ ...styles.fontStyle, textAlign: "left" }}>{getBlankWord(t('purchase.mainCurPrice', { cur: mainCur }) + t('purchase.total')) + "：" + numFormmater(totalMainPrice)}</Text></View>
            </View> */}
        </View>
        {/* 条约 */}
        {/* {addRule && pdx === resRows.length - 1 && <View style={{ paddingTop: 1, paddingBottom: 0 }}>
            <View style={[styles.ruleText, styles.ruleTextTitle, styles.ruleMv12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('采购订单交易条约', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleTextTitle]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('HỢP ĐỒNG MUA HÀNG – ĐIỀU KHOẢN GIAO DỊCH', true)}</Text>
            </View>
            <View style={styles.ruleDivider} />
            <View style={[styles.ruleText, styles.ruleTextTitle]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('1. 分项适用原则', true)}</Text>
            </View>
            <View style={[styles.ruleText]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('若本采购订单包含多个商品项目（多行物料），各项目在交期、品质、验收、付款及违约责任上独立适用本条约。任一项目的逾期、品质问题或其他违约，仅对该项目本身追究责任并计算相应违约金及损失，不影响订单内其他未违约项目的正常交货、验收及付款。采购方有权视各项目物料之间的关联情况及生产需求，决定是否接受合格项目。', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleTextTitle, styles.ruleMv12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('1. Nguyên tắc áp dụng theo từng hạng mục', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('Trường hợp đơn đặt hàng mua này bao gồm nhiều hạng mục hàng hóa (nhiều dòng vật tư), thì các điều khoản về thời hạn giao hàng, chất lượng, nghiệm thu, thanh toán và trách nhiệm vi phạm sẽ được áp dụng độc lập cho từng hạng mục.', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('Việc giao hàng chậm, phát sinh vấn đề về chất lượng hoặc các hành vi vi phạm khác của bất kỳ hạng mục nào chỉ làm phát sinh trách nhiệm đối với chính hạng mục đó; tiền phạt vi phạm và các khoản bồi thường thiệt hại sẽ được tính riêng cho hạng mục vi phạm, không ảnh hưởng đến việc giao hàng, nghiệm thu và thanh toán bình thường đối với các hạng mục khác trong đơn hàng không có vi phạm.', true)}</Text>
            </View>
            <View style={[styles.ruleText]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('Bên mua có quyền, căn cứ vào mức độ liên quan giữa các hạng mục vật tư và nhu cầu sản xuất thực tế, quyết định có chấp nhận nghiệm thu và tiếp nhận các hạng mục đạt yêu cầu hay không.', true)}</Text>
            </View>
            <View style={styles.ruleDivider} />
            <View style={[styles.ruleText, styles.ruleTextTitle]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('2. 交期', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('供应商自收到本采购订单后24小时内未就订单内容（包括各项目的交期、数量、规格等）提出书面异议的，视为接受本订单全部内容。接受订单后，供应商不得以任何理由拒绝交货或要求变更订单内容，必须按订单载明的交货日期或采购方另行发出的送货通知日期，就各项目分别按期交货。', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleTextTitle]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('对于订单中任一项目（行项）逾期交货的，按下列方式处理：', true)}</Text>
            </View>
            <View style={[styles.ruleUlContainer]} >
              <View style={styles.ruleUl}>
                <View style={styles.ruleDot} />
              </View>
              <View style={[styles.ruleRow, styles.ruleText, styles.ruleMb12]}>
                <Text style={[styles.ruleTextW100]}>{getBlankWord('该项目每逾期一次（指未按约定日期交货），采购方有权向供应商收取 USD 300.00元违约金（仅针对该逾期项目，不涉及订单内其他项目）。', true)}</Text>
              </View>
            </View>
            <View style={[styles.ruleUlContainer]} >
              <View style={styles.ruleUl}>
                <View style={styles.ruleDot} />
              </View>
              <View style={[styles.ruleRow, styles.ruleText, styles.ruleMb12]}>
                <Text style={[styles.ruleTextW100]}>{getBlankWord('若该项目的逾期行为直接导致采购方生产停线或转线的，停线工时费按 RMB 60.00元/人/小时 另行计算，采购方将针对该逾期项目开具异常工时结算单，相应费用由供应商承担。', true)}</Text>
              </View>
            </View>
            <View style={[styles.ruleUlContainer]} >
              <View style={styles.ruleUl}>
                <View style={styles.ruleDot} />
              </View>
              <View style={[styles.ruleRow, styles.ruleText, styles.ruleMb12]}>
                <Text style={[styles.ruleTextW100]}>{getBlankWord('若该项目的逾期行为直接导致采购方从其他渠道调货的，调货产生的差价（含运输、仓储等合理费用）按实另行计算，概由供应商承担（仅针对该逾期项目对应的货物数量及替代采购成本）。', true)}</Text>
              </View>
            </View>
            <View style={[styles.ruleText, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('上述所有违约金及费用，采购方有权在应付供应商的货款中优先扣除；若应付货款不足以抵扣的，可以直接向供应商追偿。', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('未逾期的项目，供应商仍应按约定日期交货，采购方应正常验收并支付相应货款，不得以存在其他逾期项目为由拒收或拒付。', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleTextTitle, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('2. Thời hạn giao hàng', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('Trong vòng 24 giờ kể từ khi nhận được Đơn đặt hàng này, nếu Nhà cung cấp không có ý kiến phản đối bằng văn bản đối với nội dung đơn hàng (bao gồm thời hạn giao hàng, số lượng, quy cách của từng hạng mục), thì được xem là đã chấp thuận toàn bộ nội dung của đơn hàng.', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('Sau khi chấp thuận đơn hàng, Nhà cung cấp không được từ chối giao hàng hoặc yêu cầu thay đổi nội dung đơn hàng với bất kỳ lý do nào, và phải thực hiện giao hàng đúng thời hạn đối với từng hạng mục theo ngày giao hàng ghi trên đơn hàng hoặc theo ngày giao hàng được Bên mua thông báo riêng bằng văn bản.', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('Đối với bất kỳ hạng mục nào (dòng hàng) trong đơn hàng bị giao hàng chậm, sẽ được xử lý như sau:', true)}</Text>
            </View>
            <View style={[styles.ruleUlContainer]} >
              <View style={styles.ruleUl}>
                <View style={styles.ruleDot} />
              </View>
              <View style={[styles.ruleRow, styles.ruleText, styles.ruleMb12]}>
                <Text style={[styles.ruleTextW100]}>{getBlankWord('Mỗi lần giao hàng chậm (không giao hàng đúng ngày đã thỏa thuận) đối với hạng mục đó, Bên mua có quyền thu của Nhà cung cấp khoản phạt vi phạm là 300,00 USD (chỉ áp dụng đối với hạng mục giao hàng chậm, không liên quan đến các hạng mục khác trong cùng đơn hàng).', true)}</Text>
              </View>
            </View>
            <View style={[styles.ruleUlContainer]} >
              <View style={styles.ruleUl}>
                <View style={styles.ruleDot} />
              </View>
              <View style={[styles.ruleRow, styles.ruleText, styles.ruleMb12]}>
                <Text style={[styles.ruleTextW100]}>{getBlankWord('Nếu việc giao hàng chậm của hạng mục đó trực tiếp dẫn đến việc Bên mua phải ngừng dây chuyền sản xuất hoặc chuyển đổi dây chuyền sản xuất, chi phí thời gian ngừng sản xuất sẽ được tính riêng theo mức 60,00 Nhân dân tệ (RMB)/người/giờ. Bên mua sẽ lập Biên bản xác nhận giờ công bất thường đối với hạng mục giao hàng chậm và toàn bộ chi phí phát sinh sẽ do Nhà cung cấp chịu.', true)}</Text>
              </View>
            </View>
            <View style={[styles.ruleUlContainer]} >
              <View style={styles.ruleUl}>
                <View style={styles.ruleDot} />
              </View>
              <View style={[styles.ruleRow, styles.ruleText, styles.ruleMb12]}>
                <Text style={[styles.ruleTextW100]}>{getBlankWord('Nếu việc giao hàng chậm của hạng mục đó trực tiếp khiến Bên mua phải mua hàng thay thế từ nguồn khác, thì phần chênh lệch giá phát sinh (bao gồm nhưng không giới hạn ở chi phí vận chuyển, lưu kho và các chi phí hợp lý khác) sẽ được tính theo thực tế và do Nhà cung cấp chịu hoàn toàn. Khoản chi phí này chỉ áp dụng đối với số lượng hàng hóa của hạng mục bị giao hàng chậm và chi phí mua thay thế tương ứng.', true)}</Text>
              </View>
            </View>
            <View style={[styles.ruleText, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('Tất cả các khoản phạt vi phạm và chi phí nêu trên, Bên mua có quyền ưu tiên khấu trừ trực tiếp vào các khoản thanh toán hàng hóa phải trả cho Nhà cung cấp. Trường hợp số tiền phải thanh toán không đủ để bù trừ, Bên mua có quyền yêu cầu Nhà cung cấp thanh toán phần còn thiếu.', true)}</Text>
            </View>
            <View style={[styles.ruleText]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('Đối với các hạng mục không bị giao hàng chậm, Nhà cung cấp vẫn phải giao hàng đúng thời hạn theo thỏa thuận; Bên mua có trách nhiệm nghiệm thu và thanh toán bình thường cho các hạng mục đó, và không được từ chối nhận hàng hoặc từ chối thanh toán với lý do tồn tại các hạng mục khác đang bị giao hàng chậm.', true)}</Text>
            </View>
            <View style={styles.ruleDivider} />
            <View style={[styles.ruleText, styles.ruleTextTitle]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('3. 品质', true)}</Text>
            </View>
            <View style={[styles.ruleText]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('经检验发现某一项目的品质不良或规格不符的，供应商应在24小时内安排处理（包括但不限于换货、维修），并确保该项目如期交货。因此造成该项目交货逾期的，按本条约第2条处理。', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('如因同一项目的品质问题导致采购方多次检验的，每次检验按 RMB 60.00元/人/小时 计算额外费用，采购方将针对该项目开具异常工时结算单，费用由供应商承担。', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleTextTitle]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('3. Chất lượng', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('Trường hợp qua kiểm tra phát hiện một hạng mục hàng hóa có chất lượng không đạt yêu cầu hoặc không phù hợp với quy cách, thông số kỹ thuật đã thỏa thuận, Nhà cung cấp phải bố trí xử lý trong vòng 24 giờ kể từ khi nhận được thông báo của Bên mua, bao gồm nhưng không giới hạn ở việc đổi hàng, sửa chữa hoặc các biện pháp khắc phục khác, đồng thời phải bảo đảm hạng mục đó được giao đúng thời hạn theo yêu cầu.', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('Trường hợp việc xử lý các vấn đề về chất lượng của hạng mục đó dẫn đến chậm giao hàng, thì hành vi chậm giao hàng sẽ được xử lý theo quy định tại Điều 2 (Thời hạn giao hàng) của các điều khoản này.', true)}</Text>
            </View>
            <View style={[styles.ruleText]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('Nếu do vấn đề chất lượng của cùng một hạng mục mà Bên mua phải tiến hành kiểm tra, đánh giá nhiều lần, thì đối với mỗi lần kiểm tra phát sinh thêm, Bên mua có quyền tính chi phí bổ sung theo mức 60,00 Nhân dân tệ (RMB)/người/giờ. Bên mua sẽ lập Biên bản xác nhận giờ công bất thường đối với hạng mục liên quan và toàn bộ chi phí phát sinh sẽ do Nhà cung cấp chịu.', true)}</Text>
            </View>
            <View style={styles.ruleDivider} />
            <View style={[styles.ruleText, styles.ruleTextTitle]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('4. 协定', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('供应商接受本订单的，应于2日内由其指定核准人签字并加盖公章后回传至采购方。供应商签回订单后，如因供应商原因（包括但不限于品质、交期、响应度）或发生其他不可抗力事件, 导致采购方需要取消该项目，采购方有权决定取消部分或多个项目并有权保留订单内其他项目继续履行，因此给采购方造成的损失由供应商承担。', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleTextTitle, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('4. Xác nhận đơn hàng', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('Sau khi chấp thuận Đơn đặt hàng này, Nhà cung cấp phải hoàn tất việc ký xác nhận bởi người có thẩm quyền được chỉ định và đóng dấu pháp nhân của doanh nghiệp, đồng thời gửi lại bản xác nhận cho Bên mua trong vòng 02 (hai) ngày kể từ ngày nhận được đơn hàng.', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('Sau khi Nhà cung cấp đã ký xác nhận và gửi lại đơn hàng, nếu vì các nguyên nhân thuộc trách nhiệm của Nhà cung cấp, bao gồm nhưng không giới hạn ở chất lượng, tiến độ giao hàng, mức độ phản hồi và phối hợp, hoặc do xảy ra sự kiện bất khả kháng, dẫn đến việc Bên mua cần phải hủy một hạng mục của đơn hàng, thì Bên mua có quyền:', true)}</Text>
            </View>
            <View style={[styles.ruleUlContainer]} >
              <View style={styles.ruleUl}>
                <View style={styles.ruleDot} />
              </View>
              <View style={[styles.ruleRow, styles.ruleText]}>
                <Text style={[styles.ruleTextW100]}>{getBlankWord('Quyết định hủy một phần hoặc nhiều hạng mục trong đơn hàng; ', true)}</Text>
              </View>
            </View>
            <View style={[styles.ruleUlContainer]} >
              <View style={styles.ruleUl}>
                <View style={styles.ruleDot} />
              </View>
              <View style={[styles.ruleRow, styles.ruleText, styles.ruleMb12]}>
                <Text style={[styles.ruleTextW100]}>{getBlankWord('Đồng thời giữ nguyên hiệu lực và tiếp tục thực hiện các hạng mục khác trong cùng đơn hàng.', true)}</Text>
              </View>
            </View>
            <View style={[styles.ruleText]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('Mọi thiệt hại phát sinh cho Bên mua do việc hủy bỏ các hạng mục nêu trên sẽ do Nhà cung cấp chịu trách nhiệm bồi thường theo thực tế.', true)}</Text>
            </View>
            <View style={styles.ruleDivider} />
            <View style={[styles.ruleText, styles.ruleTextTitle]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('5. 对账及支付方式', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('以实际交货且货物已通过采购方质检为准，双方按每月26日至次月25日为一个对账周期，对账时应按订单内各项目分别列明交货数量、合格数量、不合格数量及相应货款。供应商应于次月26日之后将对账单发送至采购方财务部，采购方予以盖章确认后发回。双方对账确认无误后，供应商在10个工作日内开具发票（可针对同一订单不同项目合并开票，但需附明细清单）。采购方收到发票后，按约定账期支付货款。', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleTextTitle, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('5. Đối chiếu công nợ và phương thức thanh toán', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('Căn cứ vào số lượng hàng hóa thực tế đã giao và đã được kiểm tra chất lượng đạt yêu cầu bởi Bên mua, hai bên thống nhất lấy khoảng thời gian từ ngày 26 hằng tháng đến ngày 25 của tháng kế tiếp làm một chu kỳ đối chiếu công nợ.', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('Khi đối chiếu, đối với từng hạng mục trong đơn hàng, phải liệt kê riêng:', true)}</Text>
            </View>
            <View style={[styles.ruleUlContainer]} >
              <View style={styles.ruleUl}>
                <View style={styles.ruleDot} />
              </View>
              <View style={[styles.ruleRow, styles.ruleText]}>
                <Text style={[styles.ruleTextW100]}>{getBlankWord('Số lượng đã giao; ', true)}</Text>
              </View>
            </View>
            <View style={[styles.ruleUlContainer]} >
              <View style={styles.ruleUl}>
                <View style={styles.ruleDot} />
              </View>
              <View style={[styles.ruleRow, styles.ruleText]}>
                <Text style={[styles.ruleTextW100]}>{getBlankWord('Số lượng đạt yêu cầu; ', true)}</Text>
              </View>
            </View>
            <View style={[styles.ruleUlContainer]} >
              <View style={styles.ruleUl}>
                <View style={styles.ruleDot} />
              </View>
              <View style={[styles.ruleRow, styles.ruleText]}>
                <Text style={[styles.ruleTextW100]}>{getBlankWord('Số lượng không đạt yêu cầu; ', true)}</Text>
              </View>
            </View>
            <View style={[styles.ruleUlContainer]} >
              <View style={styles.ruleUl}>
                <View style={styles.ruleDot} />
              </View>
              <View style={[styles.ruleRow, styles.ruleText]}>
                <Text style={[styles.ruleTextW100]}>{getBlankWord('Giá trị tiền hàng tương ứng. ', true)}</Text>
              </View>
            </View>
            <View style={[styles.ruleText, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('Sau ngày 26 của tháng kế tiếp, Nhà cung cấp phải gửi bảng đối chiếu công nợ đến Bộ phận Tài chính của Bên mua. Sau khi Bên mua đóng dấu xác nhận, bảng đối chiếu sẽ được gửi lại cho Nhà cung cấp.', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('Sau khi hai bên xác nhận đối chiếu không có sai sót, Nhà cung cấp phải xuất hóa đơn trong vòng 10 ngày làm việc. Nhà cung cấp có thể gộp các hạng mục khác nhau thuộc cùng một đơn hàng để xuất chung một hóa đơn, nhưng phải kèm theo bảng kê chi tiết.', true)}</Text>
            </View>
            <View style={[styles.ruleText]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('Sau khi nhận được hóa đơn, Bên mua sẽ thanh toán tiền hàng theo thời hạn thanh toán đã thỏa thuận.', true)}</Text>
            </View>
            <View style={styles.ruleDivider} />
            <View style={[styles.ruleText, styles.ruleTextTitle]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('6. 验收', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('若供应商所交货物（某一项目）存在进料检验时无法立即发现的潜在瑕疵（隐蔽瑕疵），待采购方在生产使用过程中或交付终端客户后发现质量问题的，因此产生的所有损失（包括但不限于维修、更换、赔偿、诉讼、律师费等）均由该项目对应的供应商承担，供应商不得提出任何异议。其他项目不受影响。', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleTextTitle]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('6. Nghiệm thu', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('Nếu hàng hóa do Nhà cung cấp giao (thuộc một hạng mục cụ thể) có tồn tại khuyết tật tiềm ẩn (khuyết tật ẩn) mà không thể phát hiện ngay trong quá trình kiểm tra nguyên vật liệu đầu vào, và Bên mua chỉ phát hiện ra vấn đề chất lượng trong quá trình sản xuất, sử dụng hoặc sau khi đã giao sản phẩm cho khách hàng cuối cùng, thì mọi tổn thất phát sinh từ đó (bao gồm nhưng không giới hạn ở chi phí sửa chữa, thay thế, bồi thường, tố tụng, phí luật sư, v.v.) đều do Nhà cung cấp của hạng mục đó chịu trách nhiệm.', true)}</Text>
            </View>
            <View style={[styles.ruleText]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('Nhà cung cấp không được đưa ra bất kỳ khiếu nại hoặc phản đối nào đối với trách nhiệm này. Các hạng mục khác trong cùng đơn hàng sẽ không bị ảnh hưởng.', true)}</Text>
            </View>
            <View style={styles.ruleDivider} />
            <View style={[styles.ruleText, styles.ruleTextTitle]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('7. 其他', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('送货单上请务必注明采购订单号、物料编号，并加盖供应商有效公章（若涉及报关，需加盖报关专用章）。对于同一订单内不同项目的货物，建议分项列示或明确标注对应行项，以便采购方分项验收、对账及处理违约，否则，采购方有权无责拒收。', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleTextTitle, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('7. 其他（Các điều khoản khác）', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('Trên phiếu giao hàng, Nhà cung cấp bắt buộc phải ghi rõ số đơn đặt hàng mua (PO Number), mã vật liệu (Material Number) và đóng dấu công ty hợp lệ của Nhà cung cấp (nếu liên quan đến thủ tục hải quan thì phải đóng dấu chuyên dùng cho khai báo hải quan).', true)}</Text>
            </View>
            <View style={[styles.ruleText]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('Đối với hàng hóa thuộc các hạng mục khác nhau trong cùng một đơn hàng, Nhà cung cấp nên liệt kê riêng theo từng hạng mục hoặc ghi chú rõ hạng mục (dòng hàng) tương ứng, để thuận tiện cho Bên mua thực hiện việc nghiệm thu, đối chiếu công nợ và xử lý vi phạm theo từng hạng mục. Nếu không thực hiện theo yêu cầu trên, Bên mua có quyền từ chối nhận hàng mà không phải chịu bất kỳ trách nhiệm nào.', true)}</Text>
            </View>
            <View style={styles.ruleDivider} />
            <View style={[styles.ruleText, styles.ruleTextTitle]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('8. 特别注意', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('送货时，请在包装箱上粘贴采购方要求的物料标识，标识上应包含订单号及物料编号，便于分项识别，否则，采购方有权无责拒收。', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleTextTitle, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('8. Lưu ý đặc biệt', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('Khi giao hàng, Nhà cung cấp phải dán nhãn nhận diện vật liệu theo yêu cầu của Bên mua trên thùng đóng gói. Trên nhãn phải thể hiện số đơn đặt hàng (PO Number) và mã vật liệu (Material Number) để thuận tiện cho việc nhận diện và phân biệt theo từng hạng mục.Nếu không thực hiện theo yêu cầu trên, Bên mua có quyền từ chối nhận hàng mà không phải chịu bất kỳ trách nhiệm nào.', true)}</Text>
            </View>
            <View style={styles.ruleDivider} />
            <View style={[styles.ruleText, styles.ruleTextTitle]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('9. 转厂', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('如本订单项下某一项目需要办理海关转厂（深加工结转）手续的，相关费用由该项目对应的供应商承担。具体手续及所需文件双方另行协商确认。', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleTextTitle, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('9. Chuyển xưởng', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('Trường hợp một hạng mục bất kỳ thuộc Đơn đặt hàng này phải thực hiện thủ tục chuyển xưởng hải quan (chuyển tiếp sản phẩm gia công), mọi chi phí phát sinh liên quan đến việc thực hiện thủ tục đó sẽ do Nhà cung cấp tương ứng với hạng mục đó chịu trách nhiệm thanh toán.', true)}</Text>
            </View>
            <View style={[styles.ruleText]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('Các thủ tục cụ thể, hồ sơ và chứng từ cần thiết để thực hiện việc chuyển xưởng sẽ do hai bên trao đổi, thỏa thuận và xác nhận riêng.', true)}</Text>
            </View>
            <View style={styles.ruleDivider} />
            <View style={[styles.ruleText, styles.ruleTextTitle]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('10. 备品', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('送货时，供应商应按本订单中各项目的采购数量按约定提供免费备品，备品品质应与正品一致。备品随该项目正品一同交付，备品逾期按该项目正品逾期处理。', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleTextTitle, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('10. Hàng dự phòng (Hàng thay thế miễn phí)', true)}</Text>
            </View>
            <View style={[styles.ruleText, styles.ruleMb12]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('Khi giao hàng, Nhà cung cấp phải cung cấp miễn phí hàng dự phòng theo tỷ lệ hoặc số lượng đã được thỏa thuận tương ứng với số lượng mua của từng hạng mục trong Đơn đặt hàng này.', true)}</Text>
            </View>
            <View style={[styles.ruleText, { marginBottom: 20 }]}>
              <Text style={[styles.ruleTextW100]}>{getBlankWord('Chất lượng của hàng dự phòng phải bảo đảm tương đương với hàng chính phẩm. Hàng dự phòng phải được giao đồng thời với hàng chính phẩm của hạng mục tương ứng. Trường hợp hàng dự phòng được giao chậm, việc chậm giao này sẽ được xử lý như trường hợp chậm giao hàng chính phẩm của hạng mục đó.', true)}</Text>
            </View>
          </View>} */}
        {addRule && <View style={{ paddingTop: 1, paddingBottom: 0 }}>
          {
            purchaseRule.filter(rule => printLangs.includes(rule.key as UserLangEnum)).map(rule => {
              let lins = rule.value.split('\n');
              return lins.map(text => (
                <View style={[styles.ruleText]}>
                  <Text style={[styles.ruleTextW100]}>{getBlankWord(text, true)}</Text>
                </View>
              ))
            })
          }
        </View>}
        <View style={{ paddingTop: 1, display: 'flex', flexDirection: 'row' }}>
          <Text style={styles.footerItem}>{`${t('purchase.purchase', { lng: "zh" })}(${t('purchase.purchase', { lng: "vn" })}):`}</Text>
          <Text style={styles.footerItem}>{`${t('purchase.review', { lng: "zh" })}(${t('purchase.review', { lng: "vn" })}):`}</Text>
          <Text style={styles.footerItem}>{`${t('purchase.signature', { lng: "zh" })}(${t('purchase.signature', { lng: "vn" })}):`}</Text>
        </View>
        <View style={{ paddingTop: 1, display: 'flex', flexDirection: 'row' }}>
          <Text style={styles.footerItem}>{`${t('purchase.createTime', { lng: "zh" })}(${t('purchase.createTime', { lng: "vn" })}): ${today}`}</Text>
          <Text style={styles.footerItem}>{`${t('purchase.reviewTime', { lng: "zh" })}(${t('purchase.reviewTime', { lng: "vn" })}):`}</Text>
          <Text style={styles.footerItem}>{`${t('purchase.sigTime', { lng: "zh" })}(${t('purchase.sigTime', { lng: "vn" })}):`}</Text>
        </View>
        <Image src={picUrl || zioncom} style={{
          position: "absolute",
          top: 24,
          left: 30,
          flexGrow: 1,
          height: 30,
          objectFit: "scale-down"
        }} />
        {/* 添加分页 */}
        <Text style={{ ...styles.fontStyle, letterSpacing: 0, ...styles.footer, zIndex: 99 }} render={({ pageNumber, totalPages }) => {
          return `P ${pageNumber} / ${totalPages}`
        }}>
        </Text>
      </Page>
    </Document >
  }, [picUrl, name, address, mainCur, printLangs, getSummary])

  const MyWareDocument = useCallback((datas: CodeUrlOrder[]) => {
    let seq = 0;
    const { num, totalPrice, totalMainPrice, orderTime, payMethod, today } = getSummary(datas);
    let supplier = datas[0]?.order?.supplier;
    let nameTemp = supplier.name || supplier.shortName;
    let supplierName = !!!nameTemp ? supplier.code : `${nameTemp}(${supplier.code})`
    let supplierContact = supplier?.contact || ''
    let billingDays = supplier.billingDays ? supplier.billingDays + t('requirement.day') : ''
    return <Document>
      <Page orientation="portrait" size="A4" style={{ ...styles.page }} wrap>
        <View fixed>
          <View><Text style={{ ...styles.fontStyle, ...styles.pageTitle }}>
            {`${name}`}
          </Text></View>
          <View><Text style={{ ...styles.fontStyle, ...styles.pageTitle, fontSize: 10 }}>
            {`${t('purchase.purchaseList', { lng: "zh" })}/${t('purchase.purchaseList', { lng: "vn" })}`}
          </Text></View>          </View>
        <View style={{ display: 'flex', justifyContent: "flex-start", flexDirection: 'row', flexWrap: "wrap", paddingTop: 1, paddingLeft: 100, }} fixed >
          <Text style={styles.headerItem}>
            {`${t('purchase.orderTime1', { lng: "zh" })}(${t('purchase.orderTime1', { lng: "vn" })}): ${orderTime}`}
          </Text>
          <Text style={styles.headerItem}>
            {`${t('requirement.supplier', { lng: "zh" })}(${t('requirement.supplier', { lng: "vn" })}): ${supplierName}`}
          </Text>
          <Text style={styles.headerItem}>
            {`${t('sale.contact', { lng: "zh" })}(${t('sale.contact', { lng: "vn" })}): ${supplierContact}`}
          </Text>
          <Text style={styles.headerItem}>
            {`${t('purchase.payMethodName', { lng: "zh" })}(${t('purchase.payMethodName', { lng: "vn" })}): ${payMethod}`}
          </Text>
          <Text style={styles.headerItem}>
            {`${t('purchase.billingDay', { lng: "zh" })}(${t('purchase.billingDay', { lng: "vn" })}): ${billingDays}`}
          </Text>
          <Text style={styles.headerItem}>
            {'TEL: 36861188'}
          </Text>
          <Text style={styles.headerItem}>
            {'FAX: 36861133'}
          </Text>
          <Text style={styles.headerItem}>
            {`${t('purchase.sendAds', { lng: "zh" })}(${t('purchase.sendAds', { lng: "vn" })}): ${address}`}
          </Text>
        </View>
        <View style={{ ...styles.table, flexShrink: 0 }}>
          {/* 表头 */}
          <View style={{ flexDirection: 'row' }} fixed render={({ pageNumber }) => {
            return pageNumber === 1 ? null : <View style={{ height: 1, backgroundColor: "#000", width: "100%" }} />
          }}></View>
          <View style={{ flexDirection: 'row', borderLeftWidth: 1 }} fixed>
            <View style={{ ...styles.tableCol, width: '8%' }}>
              <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell }}>{getBlankWord(t('product.serialNum', { lng: "zh" }), true)}</Text>
              <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell }}>{getBlankWord(t('product.serialNum', { lng: "vn" }), true)}</Text>
            </View>
            <View style={{ ...styles.tableCol, width: '14%' }}>
              <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell }}>{getBlankWord(t('purchase.orderNum', { lng: "zh" }), true)}</Text>
              <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell }}>{getBlankWord(t('purchase.orderNum', { lng: "vn" }), true)}</Text>
            </View>
            <View style={{ ...styles.tableCol, width: '15%' }}>
              <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell }}>{getBlankWord(t('purchase.printCode', { lng: "zh" }), true)}</Text>
              <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell }}>{getBlankWord(t('purchase.printCode', { lng: "vn" }), true)}</Text>
            </View>
            <View style={{ ...styles.tableCol, width: '12%' }}>
              <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell }}>{getBlankWord(t('purchase.printName', { lng: "zh" }), true)}</Text>
              <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell }}>{getBlankWord(t('purchase.printName', { lng: "vn" }), true)}</Text>
            </View>
            <View style={{ ...styles.tableCol, width: '22%' }}>
              <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell }}>{getBlankWord(t('purchase.printModel', { lng: "zh" }), true)}</Text>
              <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell }}>{getBlankWord(t('purchase.printModel', { lng: "vn" }), true)}</Text>
            </View>
            <View style={{ ...styles.tableCol, width: '8%' }}>
              <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell }}>{getBlankWord(t('purchase.printUnit', { lng: "zh" }), true)}</Text>
              <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell }}>{getBlankWord(t('purchase.printUnit', { lng: "vn" }), true)}</Text>
            </View>
            <View style={{ ...styles.tableCol, width: '21%', borderRightWidth: 1 }}>
              <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell }}>{getBlankWord(t('purchase.printNum', { lng: "zh" }), true)}</Text>
              <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell }}>{getBlankWord(t('purchase.printNum', { lng: "vn" }), true)}</Text>
            </View>
          </View>
          {/* 表身 */}
          {datas.map((item, rowIndex) => (
            <View style={{ ...styles.tableRow, borderLeftWidth: 1 }} key={rowIndex} wrap={false}>
              <View style={{ ...styles.tableCol, width: '8%', flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                <Text style={styles.fontStyle}>{++seq}</Text></View>

              <View style={{ ...styles.tableCol, ...styles.noCell, width: '14%' }}>
                {item.codeUrl && <Image src={item.codeUrl} style={{ width: "90%", marginBottom: 2, marginTop: 4 }} />}
                <Text style={[styles.fontStyle, { letterSpacing: -0.5, fontSize: item.codeUrl ? 8 : 10 }]}>{getBlankWord(item.number ?? '', true)}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '15%', letterSpacing: -1 }}><Text style={styles.fontStyle}>{getBlankWord(item.product?.code ?? '', true)}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '12%', letterSpacing: -1 }}><Text style={styles.fontStyle}>{getBlankWord(item.product?.name ?? '', true)}</Text></View>
              <View style={{ ...styles.tableCol, width: '22%', letterSpacing: -1 }}><Text style={{ ...styles.fontStyle, textAlign: 'left', letterSpacing: -1 }}>{getBlankWord(item.product?.model ?? '', true)}</Text></View>
              <View style={{ ...styles.tableCol, width: '8%', letterSpacing: -1 }}><Text style={styles.fontStyle}>{getBlankWord(item.product?.uom ?? '')}</Text></View>
              <View style={{ ...styles.tableCol, width: '21%', borderRightWidth: 1, letterSpacing: -1 }}><Text style={styles.fontStyle}>{getBlankWord(numFormmater(item.count), true)}</Text></View>
            </View>
          ))}
          {/* 合计 */}
          <View style={{ ...styles.tableRow, borderLeftWidth: 1 }}>
            <View style={{ ...styles.tableCol, width: '8%', fontSize: 8 }}></View>
            <View style={{ ...styles.tableCol, width: '14%', fontSize: 8 }}></View>
            <View style={{ ...styles.tableCol, width: '15%', fontSize: 8 }}></View>
            <View style={{ ...styles.tableCol, width: '12%', fontSize: 8 }}></View>
            <View style={{ ...styles.tableCol, width: '22%', fontSize: 8 }}></View>
            <View style={{ ...styles.tableCol, width: '29%', fontSize: 8 }}><Text style={{ ...styles.fontStyle, textAlign: "left" }}>
              {getBlankWord(`${t('purchase.numAdds', { lng: "zh" })}(${t('purchase.numAdds', { lng: "vn" })}): ${numFormmater(num)}`, true)}
            </Text></View>
          </View>
        </View>
        <View style={{ paddingTop: 1, display: 'flex', flexDirection: 'row' }}>
          <Text style={styles.footerItem}>{`${t('purchase.purchase', { lng: "zh" })}(${t('purchase.purchase', { lng: "vn" })}):`}</Text>
          <Text style={styles.footerItem}>{`${t('purchase.review', { lng: "zh" })}(${t('purchase.review', { lng: "vn" })}):`}</Text>
          <Text style={styles.footerItem}>{`${t('purchase.signature', { lng: "zh" })}(${t('purchase.signature', { lng: "vn" })}):`}</Text>
        </View>
        <View style={{ paddingTop: 1, display: 'flex', flexDirection: 'row' }}>
          <Text style={styles.footerItem}>{`${t('purchase.createTime', { lng: "zh" })}(${t('purchase.createTime', { lng: "vn" })}): ${today}`}</Text>
          <Text style={styles.footerItem}>{`${t('purchase.reviewTime', { lng: "zh" })}(${t('purchase.reviewTime', { lng: "vn" })}):`}</Text>
          <Text style={styles.footerItem}>{`${t('purchase.sigTime', { lng: "zh" })}(${t('purchase.sigTime', { lng: "vn" })}):`}</Text>
        </View>
        <Image src={picUrl || zioncom} style={{
          position: "absolute",
          top: 24,
          left: 30,
          flexGrow: 1,
          height: 30,
          objectFit: "scale-down"
        }} fixed />
        {/* 添加分页 */}
        <Text style={{ ...styles.fontStyle, letterSpacing: 0, ...styles.footer, zIndex: 99 }} render={({ pageNumber, totalPages }) => {
          return `P ${pageNumber} / ${totalPages}`
        }}>
        </Text>
      </Page>
    </Document>
  }, [picUrl, name, address, mainCur, getSummary])

  const handleUploadPDF = async (type: "WARE" | "NORM", addRule: boolean) => {
    setOpenOption(false);
    if (selectedRows.length <= 0) {
      return notification.error({
        placement: 'top',
        message: t('common.addError'),
        description: t('purchase.printSelectTip'),
      });
    }
    let valid = clickValid();
    if (!valid) return notification.error({ message: t('purchase.selectPurErrorTip') });

    // 打印前做最终兜底，避免已拒绝订单进入 PDF 和 purchasePrint 记录。
    const { validRows, rejectedCount } = splitPrintableRows(selectedRows);
    if (validRows.length <= 0) {
      return notification.warning({
        placement: 'top',
        message: t('purchase.printRejectedBlocked'),
        description: t('purchase.printRejectedBlockedDesc'),
      });
    }

    // 有部分拒绝单时，提示用户实际参与生成的数据量。
    if (rejectedCount > 0) {
      notification.warning({
        placement: 'top',
        message: t('purchase.printRejectedFiltered'),
        description: t('purchase.printRejectedFilteredDesc', {
          rejectedCount,
          validCount: validRows.length
        }),
      });
    }
    handleStart()
    setLoading(true)
    setProgress(0);
    try {
      let codeRows: CodeUrlOrder[] = [];
      let sortedRows = [...validRows].sort((a, b) => a.number.localeCompare(b.number))

      for (let i = 0; i < sortedRows.length; i++) {
        let codeObj = type === "NORM" ? (await handleCode(sortedRows[i]?.number)) : (await handleQrCode(sortedRows[i]?.number));
        let codeRow: CodeUrlOrder = {
          ...sortedRows[i],
          codeStr: codeObj.codeStr,
          codeUrl: codeObj.codeUrl
        }
        codeRows.push({ ...codeRow })
      }

      // 按 supplierId + currency + payMethod 联合分组，三个字段完全相同的数据生成同一个 PDF
      const groupMap = new Map<string, CodeUrlOrder[]>();
      for (const row of codeRows) {
        const key = `${row.order?.supplierId ?? ''}__${row.order?.currency ?? ''}__${row.order?.payMethod ?? ''}`;
        const group = groupMap.get(key);
        if (group) group.push(row);
        else groupMap.set(key, [row]);
      }
      const resList: CodeUrlOrder[][] = Array.from(groupMap.values());

      // 每组生成一个 PDF Blob
      const blobs: Blob[] = [];
      for (const group of resList) {
        const pdfDoc = type === "NORM" ? MyNormalDocument(group, addRule) : MyWareDocument(group);
        blobs.push(await pdf(pdfDoc).toBlob());
      }

      if (previewMode) {
        // 预览模式只需使用第一组 PDF
        const pdfUrl = URL.createObjectURL(blobs[0]);
        // 创建一个 iframe 并将 PDF URL 作为其源
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = 'none';
        iframe.src = pdfUrl;
        // 将 iframe 添加到 body 中
        document.body.appendChild(iframe);

        iframe.onload = () => {
          setOpenPrinterLoading(false);
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        };
        setLoading(false);
        return
      }

      let resData: ResPdfIdType = [];
      let allLen = resList.length
      // 逐组上传并登记打印记录
      for (let i = 0; i < allLen; i++) {
        const group = resList[i];
        const blob = blobs[i];
        const groupSupplierName = group[0]?.order?.supplier?.name ?? group[0]?.order?.supplier?.shortName ?? '';
        const res = await upload({ name: `${groupSupplierName}-${dayjs().format('YYYY-MM-DD')}-${i + 1}.pdf`, size: blob.size, file: blob });
        let printOrderIdRes = await api.purchasePrint({
          supplierId: group[0]?.order?.supplierId ?? '',
          fileId: res?.id ?? '',
          desc: '',
          items: group.map(item => ({ purItemId: item.id, desc: '' }))
        });
        group.forEach((item) => {
          resData.push({ id: item.id, fileId: res?.id ?? '', printOrderId: printOrderIdRes.id })
        });
        setProgress(Math.round((i + 1) / allLen * 100));
      }
      setLoading(false);
      setResPdfIds(resData);
      setOpenDownload(true);
    } catch (error) {
      console.error('上传失败:', error);
      const map: {
        [key in number]: string;
      } = {
        71803: t('purchase.printedTip'),
        10010: t('common.networkError'),
      };
      notification.error({
        placement: 'top',
        message: t('common.addError'),
        description: map[error as number] ?? t('common.serverError'),
      });
      setLoading(false);
      handleEnd();
    }
  };

  const handleCancelDownload = () => {
    setOpenDownload(false);
    handleOk(resPdfIds);
    handleEnd();
  };

  const handleOkDownload = async () => {
    let fileIds: string[] = [];
    resPdfIds.forEach(data => {
      if (!fileIds.includes(data.fileId)) fileIds.push(data.fileId)
    });
    if (fileIds.length === 1) downloadFile(fileIds[0])
    else downloadFileBatch(fileIds);
    handleCancelDownload();
  };

  const handleNormal = () => {
    if (selectedRows.length <= 0 && !previewMode) {
      return notification.error({
        message: t('common.addError'),
        description: t('purchase.printSelectTip'),
      });
    }
    if (printLangsOption.length > 0) setOpenOption(true)
    else {
      notification.info({ message: t('common.tip'), description: t('layout.noRuleConfig') })
      handleUploadPDF('NORM', false);
    }
  };

  return (
    <Fragment>
      {
        previewMode ? <Button type="primary" loading={loading || openPrinterLoading} onClick={() => handleNormal()}>{t('requirement.preview')}</Button>
          : <Dropdown
            menu={{
              items: [
                {
                  key: 'NORM',
                  label: t('purchase.normalTemplate'),
                  onClick: handleNormal,

                },
                {
                  key: 'WARE',
                  label: t('purchase.warehouseTemplate'),
                  onClick: () => handleUploadPDF('WARE', false),
                },
              ],
            }}
          >
            <Button loading={loading || openPrinterLoading}>{t('purchase.printReq')}</Button>
          </Dropdown>
      }
      <Modal title={t('purchase.addRule')} open={openOption} maskClosable={false}
        width={800} closable={false}
        onCancel={() => handleUploadPDF('NORM', false)}
        onOk={() => handleUploadPDF('NORM', true)}
        okText={t('layout.printRule')}
        cancelText={t('layout.notPrintRule')}
        okButtonProps={{ disabled: printLangs.length === 0 }}
      >
        <div>{i18next.t('layout.ruleLangsPl')}</div>
        <Checkbox.Group options={printLangsOption} defaultValue={[]} onChange={handleLangs} />
      </Modal>
      {loading && !previewMode && <ExportProgressModal title={t('purchase.pdfIng') + "..."} visible progress={progress} processLabel={t('purchase.pdfIng')} />}
      {openDownload && <Modal title={
        <Space>
          <ExclamationCircleTwoTone />
          <span>{t('common.tip')}</span>
        </Space>
      } open width={600} centered maskClosable={false} onOk={handleOkDownload} onCancel={handleCancelDownload}>
        <div>{t('purchase.downloadTip')}</div>
      </Modal>}
    </Fragment>
  );
};

export default PDFGenerator;
